import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Drawer } from 'primeng/drawer';
import { Tooltip } from 'primeng/tooltip';

import { CustomerDocsService } from './customer-docs.service';

type DocsStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

export const DOCS_FRAME_TIMEOUT_MS = 15_000;

@Component({
  selector: 'app-customer-docs-launcher',
  standalone: true,
  imports: [Drawer, Tooltip],
  templateUrl: './customer-docs-launcher.component.html',
  styleUrl: './customer-docs-launcher.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDocsLauncherComponent {
  private readonly docs = inject(CustomerDocsService);
  private readonly window = inject(DOCUMENT).defaultView;

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly drawer = viewChild(Drawer);
  private readonly frame = viewChild<ElementRef<HTMLIFrameElement>>('frame');

  protected readonly open = signal(false);
  protected readonly status = signal<DocsStatus>('idle');
  protected readonly docHubUrl = signal<string | null>(null);
  protected readonly frameSettled = signal(false);
  protected readonly newTabFailed = signal(false);

  private access?: Subscription;
  private newTabAccess?: Subscription;
  private pendingTab: Window | null = null;
  private frameTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.cancel();
      this.newTabAccess?.unsubscribe();
      this.pendingTab?.close();
    });

    effect(() => {
      const frame = this.frame()?.nativeElement;
      const url = this.docHubUrl();
      // Set directly: CustomerDocsService has already allowlisted the URL.
      if (frame && url && frame.getAttribute('src') !== url) frame.src = url;
    });
  }

  protected openDocs(): void {
    this.open.set(true);
    this.requestAccess();
  }

  protected openInNewTab(): void {
    this.newTabFailed.set(false);
    // Opened synchronously so pop-up blockers allow it. The link is requested afterwards because the
    // iframe already used the first hand-off token, which only lives 60 seconds.
    const tab = this.window?.open('about:blank', '_blank') ?? null;
    if (!tab) {
      // A tab from an earlier click that is still waiting will open the documentation anyway.
      this.newTabFailed.set(!this.pendingTab);
      return;
    }
    // A quick second click must not leave the first tab stuck on about:blank.
    this.newTabAccess?.unsubscribe();
    this.pendingTab?.close();
    tab.opener = null;
    this.pendingTab = tab;
    this.newTabAccess = this.docs.getAccess().subscribe({
      next: ({ docHubUrl }) => {
        tab.location.href = docHubUrl;
        this.pendingTab = null;
      },
      error: () => {
        tab.close();
        this.pendingTab = null;
        this.newTabFailed.set(true);
      },
    });
  }

  protected retry(): void {
    this.requestAccess();
    this.focusCloseButton();
  }

  protected onDrawerShow(): void {
    const container = this.drawer()?.container;
    if (!container) return;
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-modal', 'true');
    container.setAttribute('aria-label', 'Documentation');
    this.focusCloseButton();
  }

  protected onVisibleChange(visible: boolean): void {
    this.open.set(visible);
    if (visible) return;
    this.cancel();
    if (this.status() === 'loading') this.status.set('idle');
    this.trigger().nativeElement.focus();
  }

  protected onFrameLoad(event: Event): void {
    // An iframe fires load for its initial blank page before src is set.
    if (!(event.target as HTMLIFrameElement).getAttribute('src')) return;
    this.settleFrame();
  }

  private requestAccess(): void {
    this.cancel();
    this.status.set('loading');
    this.newTabFailed.set(false);
    this.docHubUrl.set(null);
    this.frameSettled.set(false);
    this.access = this.docs.getAccess().subscribe({
      next: ({ docHubUrl }) => {
        this.docHubUrl.set(docHubUrl);
        this.status.set('ready');
        this.frameTimer = setTimeout(() => this.settleFrame(), DOCS_FRAME_TIMEOUT_MS);
      },
      // 404 means the documentation is not set up for this environment yet, which a retry cannot fix.
      error: (err: unknown) =>
        this.status.set(err instanceof HttpErrorResponse && err.status === 404 ? 'unavailable' : 'error'),
    });
  }

  private focusCloseButton(): void {
    const close = this.drawer()?.container?.querySelector<HTMLElement>('.p-drawer-close-button');
    (close?.matches('button') ? close : close?.querySelector<HTMLElement>('button'))?.focus();
  }

  private settleFrame(): void {
    clearTimeout(this.frameTimer);
    this.frameSettled.set(true);
  }

  private cancel(): void {
    this.access?.unsubscribe();
    clearTimeout(this.frameTimer);
  }
}
